import { motion } from "framer-motion";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie,
} from "recharts";
import { Reveal, CountUp, EASE } from "./common";
import { TChapter, useLang, t } from "../i18n";
import { COUNTERS, OUTPUT_BY_YEAR, INDEXATION, RESEARCH_AREAS } from "../data/cv";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-ink text-bone font-mono text-xs px-4 py-2.5 rounded-lg shadow-xl">
      <span className="text-saffron">{label}</span> — {payload[0].value} works
    </div>
  );
};

const barColor = (y) => {
  const yr = Number(y);
  if (yr >= 2026) return "#B8252A";
  if (yr >= 2023) return "#E06B26";
  if (yr >= 2019) return "#0D0E12";
  return "#9aa0ab";
};

const Metrics = () => (
  <section id="focus" className="relative bg-bone py-24 sm:py-36" data-testid="section-focus">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <TChapter num="01" k="c1" />

      <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
        <div className="lg:col-span-2">
          <Reveal>
            <p className="text-mutedink text-base sm:text-lg leading-relaxed mb-10">
              My work sits at the intersection of <span className="text-ink font-semibold">psephology, governance and AI</span> —
              from coalition stability in Bihar to LLM-based detection of communal hate speech in the 2024 Lok Sabha
              elections, and the patent-protected <span className="text-crimson font-semibold">CHUNAV</span> voter-navigation system.
            </p>
          </Reveal>

          <div className="grid grid-cols-2 gap-px bg-ink/10 border border-ink/10 rounded-2xl overflow-hidden" data-testid="metrics-counters">
            {COUNTERS.map((c, i) => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.07, duration: 0.7, ease: EASE }}
                className="bg-panel p-5 sm:p-6"
                data-testid={`counter-${i}`}
              >
                <p className="font-display font-bold text-4xl sm:text-5xl" style={{ color: c.color }}>
                  <CountUp to={c.value} suffix={c.suffix} />
                </p>
                <p className="mt-2 text-sm font-semibold text-ink leading-snug">{c.label}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-mutedink mt-1">{c.note}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 space-y-8">
          <Reveal className="bg-panel border border-ink/10 rounded-2xl p-6 sm:p-8">
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
              <h3 className="font-display text-xl sm:text-2xl font-semibold text-ink" data-testid="chart-title-output">{t(useLang().lang, "chart_output")}</h3>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">2016 — 2026 · articles + chapters + books</span>
            </div>
            <div className="h-64 sm:h-72" data-testid="chart-output-by-year">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={OUTPUT_BY_YEAR} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                  <XAxis dataKey="year" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#5C5F6A" }} axisLine={{ stroke: "rgba(13,14,18,0.15)" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#5C5F6A" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(184,37,42,0.06)" }} />
                  <Bar dataKey="works" radius={[6, 6, 0, 0]} maxBarSize={44}>
                    {OUTPUT_BY_YEAR.map((d) => (
                      <Cell key={d.year} fill={barColor(d.year)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-4 mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-mutedink">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#9aa0ab]" />Early work</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-ink" />Ph.D. years</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-saffron" />Post-Ph.D. surge</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-crimson" />PDF @ BHU</span>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-8">
            <Reveal delay={0.1} className="bg-ink text-bone rounded-2xl p-6 sm:p-8">
              <h3 className="font-display text-xl font-semibold mb-1" data-testid="chart-title-indexation">{t(useLang().lang, "chart_index")}</h3>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/50 mb-4">Across 26 publications</p>
              <div className="h-48" data-testid="chart-indexation">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={INDEXATION} dataKey="value" nameKey="name" innerRadius={52} outerRadius={80} paddingAngle={3} strokeWidth={0}>
                      {INDEXATION.map((d) => (
                        <Cell key={d.name} fill={d.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#161820", border: "1px solid rgba(246,244,239,0.15)", borderRadius: 10, fontFamily: "JetBrains Mono", fontSize: 12, color: "#F6F4EF" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {INDEXATION.map((d) => (
                  <span key={d.name} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wide text-bone/70">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.fill }} />
                    {d.name}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.18} className="bg-panel border border-ink/10 rounded-2xl p-6 sm:p-8">
              <h3 className="font-display text-xl font-semibold text-ink mb-1" data-testid="chart-title-areas">{t(useLang().lang, "chart_areas")}</h3>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink mb-6">Share of published corpus</p>
              <div className="space-y-5" data-testid="chart-research-areas">
                {RESEARCH_AREAS.map((r, i) => (
                  <div key={r.area}>
                    <div className="flex justify-between items-baseline mb-1.5">
                      <span className="text-sm font-semibold text-ink">{r.area}</span>
                      <span className="font-mono text-xs text-mutedink">{r.pct}%</span>
                    </div>
                    <div className="h-2.5 bg-sand rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${r.pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: 0.2 + i * 0.12, ease: EASE }}
                        className="h-full rounded-full"
                        style={{ background: r.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default Metrics;
